import { AssetType } from "@nendaTypes/NendaTypes";

export function dataURLtoFile(
  dataUrl: string,
  fileName: string
): File | undefined {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
}

export const fileSizeConverted = (bytes: number): string => {
  const kilobyte = 1024;
  const megabyte = kilobyte * 1024;

  if (!bytes) return "";
  if (bytes < kilobyte) return bytes + " bytes";
  if (bytes < megabyte) return Math.round(bytes / kilobyte) + "KB";
  return Math.round(bytes / megabyte) + "MB";
};

export interface ExtractedFileInfo {
  fileSize: number;
  duration: number | null;
  fileType: string;
  mediaType: AssetType.VIDEO | AssetType.IMAGE;
  dimensions: {
    width: number;
    height: number;
  };
}

export interface MediaMetadata {
  fileSize: number | null;
  duration: number | null;
  fileType: string | null;
  mediaType: AssetType.IMAGE | AssetType.VIDEO;
  dimensions: {
    width: number;
    height: number;
  };
}

type MediaSource = File | string;

function getObjectUrl(source: MediaSource): string {
  return typeof source === "string" ? source : URL.createObjectURL(source);
}

function revokeObjectUrl(source: MediaSource, url: string) {
  if (typeof source !== "string") {
    URL.revokeObjectURL(url);
  }
}

async function getVideoMetadata(
  source: MediaSource
): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const url = getObjectUrl(source);

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
      revokeObjectUrl(source, url);
    };

    video.onerror = () => {
      reject(new Error("Failed to load video metadata"));
      revokeObjectUrl(source, url);
    };

    video.src = url;
  });
}

async function getImageDimensions(
  source: MediaSource
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = getObjectUrl(source);

    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      revokeObjectUrl(source, url);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image metadata"));
      revokeObjectUrl(source, url);
    };

    img.src = url;
  });
}

export async function extractFileMetadataFromBlob(
  file: File
): Promise<ExtractedFileInfo> {
  const fileType = file.type;
  const isVideo = fileType.startsWith("video/");
  const isImage = fileType.startsWith("image/");

  if (!isVideo && !isImage) throw new Error("Unsupported file type");

  const mediaType = isVideo ? AssetType.VIDEO : AssetType.IMAGE;

  const { width, height, duration } = isVideo
    ? await getVideoMetadata(file)
    : { ...(await getImageDimensions(file)), duration: null };

  return {
    fileSize: file.size,
    duration,
    fileType,
    mediaType,
    dimensions: { width, height },
  };
}

export async function extractMediaInfoFromUrl(
  url: string,
  type: AssetType.IMAGE | AssetType.VIDEO
): Promise<MediaMetadata> {
  let fileSize: number | null = null;
  const fileType = url.split(".").pop()?.toLowerCase() || null;

  try {
    const head = await fetch(url, { method: "HEAD" });
    fileSize = Number(head.headers.get("Content-Length")) || null;
  } catch (err) {
    console.warn("Failed to fetch file size", err);
  }

  const { width, height, duration } =
    type === AssetType.VIDEO
      ? await getVideoMetadata(url)
      : { ...(await getImageDimensions(url)), duration: null };

  return {
    fileSize,
    duration,
    fileType,
    mediaType: type,
    dimensions: { width, height },
  };
}

export async function getVideoDurationFromUrl(url: string): Promise<number> {
  const { duration } = await getVideoMetadata(url);
  return duration;
}
